/**
 * Hook for generating showroom buildings using a web worker.
 *
 * The worker fetches `/public-plans` itself on `initialize` and holds the marshalled Doc
 * privately. The main thread sends full option-choice snapshots after plan sync derives them.
 *
 * **Single call site invariant**: this hook is invoked exclusively from
 * `ShowroomGenerationRuntimeProvider`. Calling it from a second mount point would send a
 * second `initialize` message to the singleton worker, doubling the `/public-plans` fetch
 * and the GLTF pipeline.
 */
import type { ClientBuilderPlanGltfTimingSpan } from "api-types/builders/buildersResponses";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { ShowroomFloorPlanPayload } from "building-types/building/showroomFloorPlanPayloadTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import shallowCompare from "common/utils/shallowCompare";
import { getShowroomWorkerBuildingGenEnabledFromSearch } from "common/utils/showroomWorkerBuildingGen";
import stableHash from "common/utils/stableHash";
import THREEAssetLoader from "gfx/THREEAssetLoader";
import type { GLTF } from "gfx/threejs/examples/jsm/loaders/GLTFLoader";
import {
  recordShowroomIntroBenchmarkPhaseFromTiming,
  recordShowroomIntroBenchmarkPhaseToNow,
} from "lib/components/debug/showroomDebugHomeFile";
import { prefetchShowroomPropAssets } from "lib/components/showroom2/building/showroomPropPrefetch";
import {
  publishShowroomMaterialWarmup,
  resetShowroomMaterialWarmup,
} from "lib/components/showroom2/materials/showroomMaterialWarmup";
import { showroomPerformance } from "lib/components/showroom2/performance/showroomPerformance";
import type {
  MainToShowroomBuildingWorkerMessage,
  PlanIdentifiers,
  ShowroomBuildingWorkerToMainMessage,
  ShowroomWorkerBuildingTiming as SourceShowroomWorkerBuildingTiming,
} from "lib/workers/showroomBuildingWorkerMessages";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type ShowroomWorkerWindow = Window & {
  showroomWorker?: Worker;
};

let nextShowroomSessionCounter = 0;

function generateSessionId(): string {
  nextShowroomSessionCounter += 1;
  return `showroom-session-${nextShowroomSessionCounter}-${Date.now()}`;
}

function logShowroomWorkerLoadError(event: ErrorEvent): void {
  console.error("showroom worker failed", event.message, event.filename, event.lineno);
}

/**
 * Returns a shared showroom worker instance. In production the worker is
 * pre-instantiated by an inline script in index.html (injected by the
 * preloadShowroomWorkerPlugin) so the worker chunk download starts in
 * parallel with the main app bundle. In dev (and as a fallback) we lazily
 * create the worker via Vite's standard URL pattern.
 */
function getOrCreateShowroomWorker(): Worker | null {
  if (typeof window === "undefined") {
    return null;
  }

  const showroomWindow = window as ShowroomWorkerWindow;
  if (showroomWindow.showroomWorker) {
    return showroomWindow.showroomWorker;
  }

  const worker = new Worker(new URL("lib/workers/showroomBuilding.worker.ts", import.meta.url), { type: "module" });
  worker.addEventListener("error", logShowroomWorkerLoadError);
  showroomWindow.showroomWorker = worker;
  return worker;
}

function shouldWarmShowroomWorker(): boolean {
  if (typeof window === "undefined") {
    return getShowroomWorkerBuildingGenEnabledFromSearch("");
  }

  return getShowroomWorkerBuildingGenEnabledFromSearch(window.location.search);
}

/**
 * Eagerly create the shared showroom worker so its script begins fetching and
 * booting before the consumer hook subscribes. Safe to call multiple times.
 */
export function initializeShowroomWorker({ force = false }: { readonly force?: boolean } = {}): void {
  if (!force && !shouldWarmShowroomWorker()) {
    return;
  }

  getOrCreateShowroomWorker();
}

function identifiersKey(identifiers: PlanIdentifiers): string {
  return `${identifiers.builderId}|${identifiers.planType}|${identifiers.planId}|${identifiers.lotId ?? ""}`;
}

function planDataKey(planData: ClientHomePlanBaseData): string {
  return `plan-data|${planData.builder.id}|${planData.homePlan.id}|${planData.homePlan.studioDocId}|${planData.homePlan.updatedAt}`;
}

function enabledFeaturesKey(enabledFeatures: GenerateBuildingFeatureSet): string {
  return stableHash(enabledFeatures);
}

function recordClientBenchmarkPhase(name: string, startTimeMs: number | null, endTimeMs: number): void {
  recordShowroomIntroBenchmarkPhaseToNow(name, "client", startTimeMs, endTimeMs);
}

function recordWorkerSourceTiming(timing: SourceShowroomWorkerBuildingTiming): void {
  // Optional spans (materialParamsResolution, sourceResponseTotal) are skipped when absent.
  const spans: Array<[string, ClientBuilderPlanGltfTimingSpan | undefined]> = [
    ["worker.source.buildingGeneration", timing.buildingGeneration],
    ["worker.source.geometryCollection", timing.geometryCollection],
    ["worker.source.materialParamsResolution", timing.materialParamsResolution],
    ["worker.source.gltfSerialization", timing.gltfSerialization],
    ["worker.source.pipelineTotal", timing.sourcePipelineTotal],
    ["worker.source.responseTotal", timing.sourceResponseTotal],
  ];
  for (const [name, span] of spans) {
    if (span) {
      recordShowroomIntroBenchmarkPhaseFromTiming({ name, source: "worker", ...span });
    }
  }
}

export interface ShowroomWorkerBuildingResultTiming extends SourceShowroomWorkerBuildingTiming {
  gltfParse: ClientBuilderPlanGltfTimingSpan;
  clientObservedTotal: ClientBuilderPlanGltfTimingSpan;
}

export interface ShowroomWorkerBuildingResult {
  gltf: GLTF | null;
  requestedOptionChoices: OptionChoices | null;
  planData: ClientHomePlanBaseData | null;
  /** Precomputed sheet-geometry floor plan from the most recent worker reply. */
  floorPlan: ShowroomFloorPlanPayload | null;
  isGenerating: boolean;
  error: string | null;
  timing: ShowroomWorkerBuildingResultTiming | null;
}

/**
 * Hook that manages the singleton showroom building worker.
 *
 * @param identifiers - Plan identifiers (builderId, planType, planId, lotId).
 * @param optionChoices - Current option choices.
 * @param enabled - Whether worker mode is active.
 * @param enabledFeatures - Building-generation feature flags to match main-thread generation.
 * @param optionChoicesReady - Whether `optionChoices` has been derived from the initialized plan data.
 * @param planDataOverride - Optional debug plan data for worker runs that do not have route identifiers.
 * @param selectedReportingUnitId - Selected unit for unit-scoped precomputed floor-plan geometry.
 */
export function useShowroomWorkerBuilding(
  identifiers: PlanIdentifiers | undefined,
  optionChoices: OptionChoices,
  enabled: boolean,
  enabledFeatures: GenerateBuildingFeatureSet,
  optionChoicesReady: boolean = true,
  planDataOverride?: ClientHomePlanBaseData | null,
  selectedReportingUnitId?: string,
): ShowroomWorkerBuildingResult {
  const effectivelyEnabled = enabled && (!!identifiers || !!planDataOverride);

  const identifierKey = useMemo(() => (identifiers ? identifiersKey(identifiers) : null), [identifiers]);
  const featureKey = enabledFeaturesKey(enabledFeatures);
  const sourceKey = useMemo(() => {
    let key: string | null;
    if (identifierKey) {
      key = identifierKey;
    } else {
      key = planDataOverride ? planDataKey(planDataOverride) : null;
    }
    return key ? `${key}|features:${featureKey}` : null;
  }, [featureKey, identifierKey, planDataOverride]);

  const workerRef = useRef<Worker | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const latestSentChoicesRef = useRef<OptionChoices | null>(null);
  const latestSentSelectedReportingUnitIdRef = useRef<string | undefined>(undefined);
  const lastIdentifierKeyRef = useRef<string | null>(null);
  const initializePostTimeRef = useRef<number | null>(null);
  const initializedReceivedTimeRef = useRef<number | null>(null);
  const generationStartTimeRef = useRef<number | null>(null);
  const updatedReceivedTimeRef = useRef<number | null>(null);
  const pendingSourceTimingRef = useRef<SourceShowroomWorkerBuildingTiming | null>(null);

  const [pendingGltf, setPendingGltf] = useState<{
    binary: ArrayBuffer;
    requestedOptionChoices: OptionChoices;
  } | null>(null);
  const [gltf, setGltf] = useState<GLTF | null>(null);
  const [requestedOptionChoices, setRequestedOptionChoices] = useState<OptionChoices | null>(null);
  const [planData, setPlanData] = useState<ClientHomePlanBaseData | null>(null);
  const [floorPlan, setFloorPlan] = useState<ShowroomFloorPlanPayload | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timing, setTiming] = useState<ShowroomWorkerBuildingResultTiming | null>(null);

  const sendUpdateOptions = useCallback(
    (choices: OptionChoices, selectedReportingUnitLabelId: string | undefined): void => {
      if (!sessionIdRef.current || !workerRef.current) {
        return;
      }

      const message: MainToShowroomBuildingWorkerMessage = {
        type: "updateOptions",
        sessionId: sessionIdRef.current,
        optionChoices: choices,
        selectedReportingUnitLabelId,
      };
      latestSentChoicesRef.current = choices;
      latestSentSelectedReportingUnitIdRef.current = selectedReportingUnitLabelId;
      generationStartTimeRef.current = performance.now();
      updatedReceivedTimeRef.current = null;
      pendingSourceTimingRef.current = null;
      resetShowroomMaterialWarmup(sessionIdRef.current);
      setIsGenerating(true);
      setError(null);
      setTiming(null);
      workerRef.current.postMessage(message);
    },
    [],
  );

  // Subscribe to worker messages while the hook is enabled.
  useEffect(() => {
    if (!effectivelyEnabled) {
      return;
    }

    const worker = getOrCreateShowroomWorker();
    if (!worker) {
      return;
    }

    workerRef.current = worker;

    const handleMessage = (event: MessageEvent<ShowroomBuildingWorkerToMainMessage>) => {
      const message = event.data;

      // Discard messages from stale sessions.
      if (message.sessionId !== sessionIdRef.current) {
        return;
      }

      switch (message.type) {
        case "initialized": {
          const initializedReceivedTime = performance.now();
          recordClientBenchmarkPhase(
            "worker.client.initializePostToInitialized",
            initializePostTimeRef.current,
            initializedReceivedTime,
          );
          initializedReceivedTimeRef.current = initializedReceivedTime;
          setPlanData(message.planData);
          setError(null);
          break;
        }

        case "materialWarmup":
          recordClientBenchmarkPhase(
            message.final
              ? "worker.client.updateOptionsPostToMaterialWarmup"
              : "worker.client.updateOptionsPostToEarlyManifest",
            generationStartTimeRef.current,
            performance.now(),
          );
          publishShowroomMaterialWarmup({
            sessionId: message.sessionId,
            materialSources: message.materialSources,
            designMaterialParamsByKey: message.designMaterialParamsByKey,
            final: message.final,
          });
          if (message.propAssetIds?.length) {
            prefetchShowroomPropAssets(message.propAssetIds);
          }
          break;

        case "updated":
          updatedReceivedTimeRef.current = performance.now();
          recordClientBenchmarkPhase(
            "worker.client.updateOptionsPostToUpdated",
            generationStartTimeRef.current,
            updatedReceivedTimeRef.current,
          );
          if (message.timing.sourceResponseTotal?.startTimeMs !== undefined) {
            recordClientBenchmarkPhase(
              "worker.client.sourceResponseEndToUpdated",
              message.timing.sourceResponseTotal.startTimeMs +
                message.timing.sourceResponseTotal.durationMs -
                performance.timeOrigin,
              updatedReceivedTimeRef.current,
            );
          }
          recordWorkerSourceTiming(message.timing);
          setPendingGltf({
            binary: message.gltfBinary,
            requestedOptionChoices: message.requestedOptionChoices,
          });
          // The refreshed floor plan follows in a separate `floorPlan` message; clear the
          // previous generation's payload so the 2D view never shows a stale plan.
          setFloorPlan(null);
          setError(null);
          pendingSourceTimingRef.current = message.timing;
          showroomPerformance((perf) => {
            perf.recordTiming("workerBuildingGeneration", message.timing.buildingGeneration);
            perf.recordTiming("workerGeometryCollection", message.timing.geometryCollection);
            perf.recordTiming("workerGltfSerialization", message.timing.gltfSerialization);
          });
          break;

        case "floorPlan":
          recordShowroomIntroBenchmarkPhaseFromTiming({
            name: "worker.source.floorPlanGeneration",
            source: "worker",
            ...message.timing,
          });
          setFloorPlan(message.floorPlan);
          break;

        case "error":
          setIsGenerating(false);
          setError(message.error);
          console.error("ShowroomWorkerBuilding: Error:", message.error);
          break;
      }
    };

    const handleError = (event: ErrorEvent) => {
      setIsGenerating(false);
      setError(event.message || "Worker error");
      console.error("ShowroomWorkerBuilding: Worker error:", event.message);
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);

    return () => {
      // Keep the singleton alive across re-mounts; just detach this subscription
      // so stale callbacks don't fire and discarded sessions don't leak handlers.
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      workerRef.current = null;
    };
  }, [effectivelyEnabled, sendUpdateOptions]);

  // Open / re-open a session whenever the identifier key changes.
  useEffect(() => {
    if (!effectivelyEnabled || !sourceKey) {
      // Reset local state when going from enabled -> disabled or identifier-cleared.
      if (sessionIdRef.current && workerRef.current) {
        workerRef.current.postMessage({ type: "reset", sessionId: sessionIdRef.current });
      }
      resetShowroomMaterialWarmup(sessionIdRef.current ?? undefined);
      sessionIdRef.current = null;
      latestSentChoicesRef.current = null;
      latestSentSelectedReportingUnitIdRef.current = undefined;
      lastIdentifierKeyRef.current = null;
      initializePostTimeRef.current = null;
      initializedReceivedTimeRef.current = null;
      generationStartTimeRef.current = null;
      updatedReceivedTimeRef.current = null;
      pendingSourceTimingRef.current = null;
      setGltf(null);
      setRequestedOptionChoices(null);
      setPlanData(null);
      setFloorPlan(null);
      setPendingGltf(null);
      setIsGenerating(false);
      setError(null);
      setTiming(null);
      return;
    }

    if (lastIdentifierKeyRef.current === sourceKey) {
      return;
    }

    const worker = getOrCreateShowroomWorker();
    if (!worker) {
      return;
    }

    // Tear down the previous session.
    if (sessionIdRef.current) {
      worker.postMessage({ type: "reset", sessionId: sessionIdRef.current });
      resetShowroomMaterialWarmup(sessionIdRef.current);
    }

    const sessionId = generateSessionId();
    sessionIdRef.current = sessionId;
    lastIdentifierKeyRef.current = sourceKey;
    latestSentChoicesRef.current = null;
    latestSentSelectedReportingUnitIdRef.current = undefined;
    initializePostTimeRef.current = null;
    initializedReceivedTimeRef.current = null;
    generationStartTimeRef.current = null;
    updatedReceivedTimeRef.current = null;
    pendingSourceTimingRef.current = null;

    // Reset per-session UI state.
    setGltf(null);
    setRequestedOptionChoices(null);
    setPlanData(null);
    setFloorPlan(null);
    setPendingGltf(null);
    setIsGenerating(true);
    setError(null);
    setTiming(null);

    let message: MainToShowroomBuildingWorkerMessage;
    if (planDataOverride) {
      message = {
        type: "initialize",
        sessionId,
        planData: planDataOverride,
        enabledFeatures,
      };
    } else {
      assertIsNotNullOrUndefined(identifiers);
      message = {
        type: "initialize",
        sessionId,
        identifiers,
        enabledFeatures,
      };
    }
    initializePostTimeRef.current = performance.now();
    worker.postMessage(message);
  }, [effectivelyEnabled, enabledFeatures, identifiers, planDataOverride, sourceKey]);

  // Push option-choice changes within an active session.
  useEffect(() => {
    if (!effectivelyEnabled || !optionChoicesReady || !planData || !sessionIdRef.current || !workerRef.current) {
      return;
    }

    if (
      shallowCompare(latestSentChoicesRef.current, optionChoices) &&
      latestSentSelectedReportingUnitIdRef.current === selectedReportingUnitId
    ) {
      return;
    }

    // Only the first option post measures "init -> first options post". initializedReceivedTimeRef
    // is not reset between updates, so recording it on later sends would span the original init to
    // the current send.
    if (latestSentChoicesRef.current === null) {
      recordClientBenchmarkPhase(
        "worker.client.initializedToUpdateOptionsPost",
        initializedReceivedTimeRef.current,
        performance.now(),
      );
    }
    sendUpdateOptions(optionChoices, selectedReportingUnitId);
  }, [effectivelyEnabled, optionChoices, optionChoicesReady, planData, selectedReportingUnitId, sendUpdateOptions]);

  // Parse GLTF binary on the main thread when it arrives from the worker.
  useEffect(() => {
    if (!pendingGltf) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        showroomPerformance((perf) => perf.start("gltfParsing"));

        const assetLoader = THREEAssetLoader.instance;
        const parseStartTime = performance.now();
        recordClientBenchmarkPhase("worker.client.updatedToParseStart", updatedReceivedTimeRef.current, parseStartTime);
        const parsed = await assetLoader.parseGLTF(pendingGltf.binary);
        const parseEndTime = performance.now();
        const gltfParse = {
          startTimeMs: performance.timeOrigin + parseStartTime,
          durationMs: parseEndTime - parseStartTime,
        };

        if (cancelled) {
          return;
        }

        showroomPerformance((perf) => perf.end("gltfParsing"));

        const sourceTiming = pendingSourceTimingRef.current;
        if (sourceTiming) {
          const clientObservedTotal =
            generationStartTimeRef.current !== null
              ? {
                  startTimeMs: performance.timeOrigin + generationStartTimeRef.current,
                  durationMs: parseEndTime - generationStartTimeRef.current,
                }
              : {
                  durationMs: sourceTiming.sourcePipelineTotal.durationMs + gltfParse.durationMs,
                };
          setTiming({
            ...sourceTiming,
            gltfParse,
            clientObservedTotal,
          });
        }
        recordClientBenchmarkPhase(
          "worker.client.updateOptionsPostToParsed",
          generationStartTimeRef.current,
          parseEndTime,
        );

        setRequestedOptionChoices(pendingGltf.requestedOptionChoices);
        setGltf(parsed);
        setIsGenerating(false);
        setError(null);
        setPendingGltf(null);
        generationStartTimeRef.current = null;
        pendingSourceTimingRef.current = null;
      } catch (err) {
        if (cancelled) {
          return;
        }

        showroomPerformance((perf) => perf.end("gltfParsing"));

        const errorMessage = err instanceof Error ? err.message : String(err);
        setIsGenerating(false);
        setError(errorMessage);
        generationStartTimeRef.current = null;
        pendingSourceTimingRef.current = null;
        console.error("ShowroomWorkerBuilding: GLTF parsing error:", errorMessage);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingGltf]);

  return useMemo(
    () => ({
      gltf,
      requestedOptionChoices,
      planData,
      floorPlan,
      isGenerating,
      error,
      timing,
    }),
    [error, floorPlan, gltf, isGenerating, planData, requestedOptionChoices, timing],
  );
}
